import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AtriumApi, { getApiErrorMessage } from "api";
import notify from "lib/notify";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";
import {
  MdLink,
  MdPeople,
  MdChurch,
  MdAttachMoney,
  MdQrCode,
  MdSettings,
  MdOpenInNew,
  MdContentCopy,
  MdTrendingUp,
  MdAccountBalance,
  MdShare,
} from "react-icons/md";
import { PageContainer, PageHeader } from "components/page";
import Card, { CardHeader } from "components/card";
import Button from "components/button";
import StatCard from "components/stat-card";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import { formatNumber } from "utils/format";

const QUICK_ACTIONS = [
  {
    to: "/admin/connect/customize",
    icon: MdSettings,
    title: "Customize Profile",
    description: "Update logo, colors, and branding",
  },
  {
    to: "/admin/connect/links",
    icon: MdLink,
    title: "Manage Links",
    description: "Add and organize your links",
  },
  {
    to: "/admin/connect/social",
    icon: MdShare,
    title: "Social Links",
    description: "Connect your social media",
  },
  {
    to: "/admin/connect/qr-code",
    icon: MdQrCode,
    title: "QR Code",
    description: "Download QR code for bulletins",
  },
  {
    to: "/admin/connect/visitors",
    icon: MdPeople,
    title: "Visitor Submissions",
    description: "View contact form submissions",
  },
  {
    to: "/admin/connect/prayers",
    icon: MdChurch,
    title: "Prayer Requests",
    description: "View and manage prayer requests",
  },
  {
    to: "/admin/connect/donations",
    icon: MdAttachMoney,
    title: "Donations",
    description: "View donation history and stats",
  },
  {
    to: "/admin/connect/funds",
    icon: MdAccountBalance,
    title: "Giving Funds",
    description: "Manage donation fund categories",
  },
  {
    to: "/admin/connect/analytics",
    icon: MdTrendingUp,
    title: "Analytics",
    description: "Track page views and conversions",
  },
];

const PAGE_DESCRIPTION = "Manage your Link Hub, visitor connections, and donations";

const ConnectDashboard = () => {
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState({
    totalLinks: 0,
    totalVisitors: 0,
    totalPrayers: 0,
    totalDonations: 0,
  });
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setProfileError(null);
    try {
      const response = await AtriumApi.get("/connect/profile");
      if (response.data.success) {
        setProfile(response.data.data);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        setProfileError(error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const [linksRes, visitorsRes, prayersRes, donationsRes] = await Promise.all([
        AtriumApi.get("/connect/links"),
        AtriumApi.get("/connect/visitors?limit=1"),
        AtriumApi.get("/connect/prayers?limit=1"),
        AtriumApi.get("/connect/donations/stats"),
      ]);

      setStats({
        totalLinks: linksRes.data.data?.length || 0,
        totalVisitors: visitorsRes.data.total || 0,
        totalPrayers: prayersRes.data.total || 0,
        totalDonations: donationsRes.data.data?.totalDonations || 0,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    track(EVENTS.CONNECT_PAGE_VIEWED);
    fetchProfile();
    fetchStats();
  }, [fetchProfile, fetchStats]);

  const publicUrl = profile?.slug ? `${window.location.origin}/connect/${profile.slug}` : "";

  const copyPublicUrl = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      notify.success("Public URL copied to clipboard");
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not copy the URL"));
    }
  };

  const header = <PageHeader title="Connect" description={PAGE_DESCRIPTION} />;

  if (loading) {
    return (
      <PageContainer>
        {header}
        <Skeleton className="h-32 w-full rounded-card" />
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full rounded-card" />
          ))}
        </div>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-20 w-full rounded-card" />
          ))}
        </div>
      </PageContainer>
    );
  }

  if (profileError) {
    return (
      <PageContainer>
        {header}
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(profileError, "Could not load your Link Hub")}
            onRetry={fetchProfile}
          />
        </Card>
      </PageContainer>
    );
  }

  if (!profile) {
    return (
      <PageContainer>
        {header}
        <Card padding="md">
          <EmptyState
            icon={MdLink}
            title="Welcome to Connect"
            description="Create your Link Hub to share important links, collect visitor information, receive prayer requests, and accept donations all in one place."
            action={
              <Button as={Link} to="/admin/connect/customize" leftIcon={<MdSettings />}>
                Create Link Hub
              </Button>
            }
          />
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {header}

      {publicUrl && (
        <Card padding="md">
          <CardHeader
            title="Your Public Link Hub"
            actions={
              <Button
                as="a"
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="ghost"
                size="sm"
                leftIcon={<MdOpenInNew />}
              >
                Visit
              </Button>
            }
          />
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
            <code className="min-w-0 flex-1 truncate rounded-control bg-surface-raised px-3 py-2 font-mono text-sm text-fg">
              {publicUrl}
            </code>
            <Button variant="secondary" leftIcon={<MdContentCopy />} onClick={copyPublicUrl}>
              Copy
            </Button>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={MdLink}
          label="Total Links"
          value={formatNumber(stats.totalLinks)}
          loading={statsLoading}
        />
        <StatCard
          icon={MdPeople}
          accent="sky"
          label="Visitor Submissions"
          value={formatNumber(stats.totalVisitors)}
          loading={statsLoading}
        />
        <StatCard
          icon={MdChurch}
          accent="slate"
          label="Prayer Requests"
          value={formatNumber(stats.totalPrayers)}
          loading={statsLoading}
        />
        <StatCard
          icon={MdAttachMoney}
          accent="gold"
          label="Total Donations"
          value={formatNumber(stats.totalDonations)}
          loading={statsLoading}
        />
      </div>

      <section className="flex flex-col gap-3">
        <h2 className="text-heading">Quick Actions</h2>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {QUICK_ACTIONS.map(({ to, icon: Icon, title, description }) => (
            <Card
              key={to}
              as={Link}
              to={to}
              padding="md"
              className="focus-ring flex-row items-center gap-4 transition-colors hover:bg-surface-subtle"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-control bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
                <Icon aria-hidden="true" className="h-5 w-5" />
              </span>
              <span className="min-w-0">
                <span className="block font-semibold text-fg">{title}</span>
                <span className="block text-sm text-fg-muted">{description}</span>
              </span>
            </Card>
          ))}
        </div>
      </section>
    </PageContainer>
  );
};

export default ConnectDashboard;
